// Device-local guest identity (client only).
//
// Before a guest signs up they get a stable, device-persisted handle like
// `guest_1a2b3c4d` that is attached to the orders and reservation requests they
// place. It is a label, not a credential: claiming stored orders/reservations
// into an account later uses the bearer tokens (order access token, reservation
// request id), never this username. When the guest finally signs up, the device
// claims everything into their account and this identity is cleared, so the
// account username replaces the temporary guest handle.

export type GuestIdentity = {
  /** Random stable id for this device's guest persona. */
  guestId: string;
  /** Public-facing handle like `guest_1a2b3c4d`. */
  username: string;
  /** Epoch ms when the identity was created. */
  createdAt: number;
};

const STORAGE_KEY = 'mnrq:guest-identity:v1';

function generateUsername(): string {
  const bytes = new Uint8Array(4);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  let hex = '';
  for (const b of bytes) hex += b.toString(16).padStart(2, '0');
  return `guest_${hex}`;
}

function safeParse(raw: string | null): GuestIdentity | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (
      parsed &&
      typeof parsed.guestId === 'string' &&
      typeof parsed.username === 'string' &&
      typeof parsed.createdAt === 'number'
    ) {
      return { guestId: parsed.guestId, username: parsed.username, createdAt: parsed.createdAt };
    }
  } catch {
    /* corrupt entry, regenerate */
  }
  return null;
}

/** Reads the device's guest identity, generating and persisting it on first use. */
export function getGuestIdentity(): GuestIdentity | null {
  if (typeof window === 'undefined') return null;
  const existing = safeParse(window.localStorage.getItem(STORAGE_KEY));
  if (existing) return existing;
  const created: GuestIdentity = {
    guestId:
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `g_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`,
    username: generateUsername(),
    createdAt: Date.now(),
  };
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(created));
  } catch {
    /* storage unavailable, in-memory only */
  }
  return created;
}

/** Convenience for callers that only want the display handle. */
export function getGuestUsername(): string | null {
  return getGuestIdentity()?.username ?? null;
}

/** Cleared once the guest signs up and their data is claimed into the account. */
export function clearGuestIdentity(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* best-effort */
  }
}
